'use client';

import clsx from 'clsx';
import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { HtmlDecoder } from '@headstartwp/core/react';
import { Data, Clock } from '@themaven-net/raven-assets/index';
import { useGaContext } from '@/context/GoogleAnalyticsContext';

import styles from './styles.module.css';
import { PollOption } from './PollOption';
import { PollFooter } from './PollFooter';
import { mapApiData, type PollApi, type PollData } from './util';

interface PollAttributes {
	id: string;
	pollData: PollData;
}

const fetchData = async (id: string): Promise<null | PollApi> => {
	try {
		const fetchURL = `/.api/polls?pollId=${id}`;
		const response = await fetch(fetchURL);

		if (!response.ok) {
			return null;
		}

		const json = await response.json();
		return json as PollApi;
	} catch (error) {
		return null;
	}
};

export const Poll: FC<PollAttributes> = ({ id, pollData }) => {
	const { description, question } = pollData;

	const [closed, setClosed] = useState(pollData.closed);
	const [closedTime, setClosedTime] = useState(pollData.closedTime);
	const [selectedOption, setSelectedOption] = useState<null | string>(null);
	const [hasVoted, setHasVoted] = useState(false);
	const [voteCount, setVoteCount] = useState(pollData.totalVotes);
	const [options, setOptions] = useState(pollData.options);
	const { sendGaEvent } = useGaContext('Poll');
	const [isDataLoaded, setIsDataLoaded] = useState(false);

	useEffect(() => {
		if (isDataLoaded || !id) {
			return;
		}

		fetchData(id)
			.then((pollApiData) => {
				const mappedData = mapApiData(pollApiData);
				if (!mappedData) {
					return;
				}

				setClosed(mappedData.closed);
				setClosedTime(mappedData.closedTime);
				setOptions(mappedData.options);
				setVoteCount(mappedData.totalVotes);
			})
			.finally(() => setIsDataLoaded(true));
	}, [id, isDataLoaded]);

	// Ensure voted state is read after data is loaded
	useEffect(() => {
		if (!isDataLoaded || !id) return;
		const storedVote = localStorage.getItem(`poll_voted:${id}`);
		if (storedVote) {
			setHasVoted(true);
			setSelectedOption(storedVote);
		}
	}, [id, isDataLoaded]);

	const handleVote = async () => {
		if (!selectedOption) {
			return;
		}

		try {
			const response = await fetch(
				`/.api/polls/vote?pollId=${id}&optionId=${selectedOption}`,
				{
					method: 'POST',
				},
			);

			if (!response.ok) {
				return;
			}

			sendGaEvent('poll-user-voted', {
				pollId: id,
				pollOption: selectedOption,
			});
		} catch (e) {
			return;
		}

		const newVoteCount = voteCount + 1;

		setHasVoted(true);
		setOptions((currOptions) =>
			currOptions.map((e) => {
				const newScore = e.id === selectedOption ? e.score + 1 : e.score;

				return {
					...e,
					percentage: ((newScore / newVoteCount) * 100).toFixed(),
					score: newScore,
				};
			}),
		);
		setVoteCount(newVoteCount);
		localStorage.setItem(`poll_voted:${id}`, selectedOption);
	};

	const ONE_MINUTE_IN_MS = 1000 * 60;
	const ONE_HOUR_IN_MS = 1000 * 60 * 60;
	const ONE_DAY_IN_MS = ONE_HOUR_IN_MS * 24;

	const getTimeLeftLabel = (): string => {
		const closedTimeTs = new Date(closedTime ?? 0).getTime();
		const currentTime = Date.now();
		const timeLeftMs = closedTimeTs - currentTime;

		// Poll should be closed in this case, but accounting for it anyway.
		if (timeLeftMs <= 0) {
			return '0 minutes left';
		}

		if (timeLeftMs <= ONE_HOUR_IN_MS) {
			const timeLeftMinutes = Math.floor(timeLeftMs / ONE_MINUTE_IN_MS);

			return timeLeftMinutes === 1
				? `${timeLeftMinutes} minute left`
				: `${timeLeftMinutes} minutes left`;
		}

		if (timeLeftMs <= ONE_DAY_IN_MS) {
			const timeLeftHours = Math.floor(timeLeftMs / ONE_HOUR_IN_MS);

			return timeLeftHours === 1
				? `${timeLeftHours} hour left`
				: `${timeLeftHours} hours left`;
		}

		const timeLeftDays = Math.floor(timeLeftMs / ONE_DAY_IN_MS);

		return timeLeftDays === 1
			? `${timeLeftDays} day left`
			: `${timeLeftDays} days left`;
	};

	return (
		<form
			className={styles.poll}
			onSubmit={(e) => {
				e.preventDefault();
				handleVote();
			}}
		>
			<div className={styles.pollHeading}>
				<div className={styles.pollDetails}>
					<h2 className={styles.pollQuestion}>
						<span className={styles.pollQuestionIcon}>
							<Data height="20" width="20" />
						</span>
						<HtmlDecoder html={question} />
					</h2>
					<p className={styles.pollDescription}>
						<HtmlDecoder html={description} />
					</p>
				</div>
				<span className={styles.pollStatus}>
					<Clock className={styles.pollStatusIcon} />
					{closed ? 'Poll Closed' : getTimeLeftLabel()}
				</span>
			</div>
			<div
				className={clsx(styles.pollOptionsContainer, {
					[styles.hasVoted]: hasVoted,
				})}
			>
				{options.map((option) => (
					<PollOption
						allScores={options.map((o) => o.score)}
						hasVoted={hasVoted}
						isClosed={closed}
						key={option.id}
						onChange={() => setSelectedOption(option.id)}
						option={option}
						selectedOption={selectedOption}
					/>
				))}
			</div>
			<PollFooter
				hasVoted={hasVoted}
				isClosed={closed}
				selectedOption={selectedOption}
				voteCount={voteCount}
			/>
		</form>
	);
};
